import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useListings } from '../../hooks/useListings';
import { useShiurNext } from '../../hooks/useShiurim';
import { pickLang, subcategoryLabel } from '../../utils/listingDisplay';
import { listingHref } from './ListingCard';
import { NextShiurText } from './ShiurTimeText';

/** "Shiurim here" on a shul page: published shiurim linked to this shul. Nothing when there are none. */
export default function ShulShiurim({ shulId }: { shulId: string }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { listings } = useListings({ category: 'shiur' });
  const here = useMemo(
    () =>
      listings
        .filter((l) => l.category === 'shiur' && l.details?.where === 'shul' && l.details.shulId === shulId)
        .sort((a, b) => pickLang(a.nameEn, a.nameHe, isHebrew).localeCompare(pickLang(b.nameEn, b.nameHe, isHebrew))),
    [listings, shulId, isHebrew]
  );
  const { nextById } = useShiurNext(here);
  if (here.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">{t('directory.shiur.show.hereTitle')}</h2>
      <ul className="space-y-2">
        {here.map((s) => {
          const giver = s.category === 'shiur' ? pickLang(s.details?.giverEn, s.details?.giverHe, isHebrew) : '';
          return (
            <li key={s.id}>
              <Link
                to={listingHref(s)}
                className="flex items-start gap-3 rounded-[16px] border border-ink/10 bg-paper p-3 hover:border-ink/30 transition-colors"
              >
                <span className="shrink-0 w-10 h-10 rounded-[10px] bg-fog flex items-center justify-center text-ink/70">
                  <BookOpen className="w-5 h-5" strokeWidth={1.5} aria-hidden />
                </span>
                <span className="min-w-0 flex-1 space-y-1">
                  <span className="block font-semibold text-ink">{pickLang(s.nameEn, s.nameHe, isHebrew)}</span>
                  <span className="block text-sm text-ash">
                    {subcategoryLabel(s, t, isHebrew)}
                    {giver ? ` · ${giver}` : ''}
                  </span>
                  <NextShiurText next={nextById[s.id]} />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
